/**
 * Puts the readable shades of the theme builder's colour on <body> (spec
 * docs/superpowers/specs/2026-09-27-app-ready-made-themes-design.md, "Ruling 5").
 *
 * The builder (components/theme-switcher.tsx) keeps doing exactly what it did: a grid colour is a
 * `color-<name>` class on <body>, the person's own colour is an inline `--primary` there, and either
 * one fills buttons, badges and accents as picked. This reads that colour and writes, beside it:
 *
 *   - `--app-builder-text`  primary written as text (4.5:1 on the page, cards and the colour's tint)
 *   - `--app-builder-ring`  the focus ring (3:1)
 *   - `--app-builder-ink`   words on a filled primary (white or near-black)
 *   - `data-builder-readable`, which switches on components/app-theme/builder-readable.css
 *
 * Only while the builder's look is on — under Custom, or for someone who never chose a theme — only for
 * a colour that is not Classic's own, and only while an app page is open: the app's theme scope runs
 * `watchBuilderReadable` and its cleanup takes everything off again, so the play area and the portal get
 * <body> as the builder alone left it.
 */
import { THEME_COLORS } from '@/lib/constants/themes'
import { APP_CUSTOM_THEME, APP_THEME_ATTRIBUTE } from './app-themes/app-themes'
import { builderShades, type BuilderShades } from './builder-readable'

export const BUILDER_READABLE_ATTRIBUTE = 'data-builder-readable'

export const BUILDER_READABLE_VARS: Record<keyof BuilderShades, `--${string}`> = {
  text: '--app-builder-text',
  ring: '--app-builder-ring',
  ink: '--app-builder-ink',
}

/** The builder's colour on <body>: its own (inline --primary) or a grid colour; null for Classic's own. */
export function builderColourOn(doc: Document = document): string | null {
  const body = doc.body
  if (!body) return null
  const own = body.style.getPropertyValue('--primary').trim()
  if (own) return own
  for (const { name, value } of THEME_COLORS) {
    if (name !== 'default' && body.classList.contains(`color-${name}`)) return value
  }
  return null
}

/** The shades to show now, or null when nothing should be written. */
function shadesNow(doc: Document): BuilderShades | null {
  const root = doc.documentElement
  // The builder's look is Custom's, and theirs who never chose: chosen Classic is the app's own colour,
  // and a ready-made theme brings its own.
  const chosen = root.getAttribute(APP_THEME_ATTRIBUTE)
  if (chosen !== null && chosen !== APP_CUSTOM_THEME) return null
  const colour = builderColourOn(doc)
  if (!colour) return null
  try {
    return builderShades(colour, root.classList.contains('dark') ? 'dark' : 'light')
  } catch {
    return null // a colour this cannot read: leave the page as the builder painted it
  }
}

/** Make <body> carry the shades the builder's colour needs now — writing only what changed. */
export function applyBuilderReadable(doc: Document = document): void {
  const body = doc.body
  if (!body) return
  const shades = shadesNow(doc)
  if (!shades) {
    clearBuilderReadable(doc)
    return
  }
  for (const key of Object.keys(BUILDER_READABLE_VARS) as Array<keyof BuilderShades>) {
    const name = BUILDER_READABLE_VARS[key]
    if (body.style.getPropertyValue(name) !== shades[key]) body.style.setProperty(name, shades[key])
  }
  if (!body.hasAttribute(BUILDER_READABLE_ATTRIBUTE)) body.setAttribute(BUILDER_READABLE_ATTRIBUTE, '')
}

/** Take the shades and the switch off <body>, leaving the builder's own classes and colour. */
export function clearBuilderReadable(doc: Document = document): void {
  const body = doc.body
  if (!body) return
  for (const name of Object.values(BUILDER_READABLE_VARS)) {
    if (body.style.getPropertyValue(name) !== '') body.style.removeProperty(name)
  }
  if (body.hasAttribute(BUILDER_READABLE_ATTRIBUTE)) body.removeAttribute(BUILDER_READABLE_ATTRIBUTE)
}

/**
 * Keep <body> in step while an app page is open: the builder's colour (body class and inline style)
 * and light/dark or a ready-made theme (<html> class and attribute). Returns the cleanup, which takes
 * everything off. Its own writes change nothing the second time round, so it never loops.
 */
export function watchBuilderReadable(doc: Document = document): () => void {
  applyBuilderReadable(doc)
  if (typeof MutationObserver !== 'function' || !doc.body) return () => clearBuilderReadable(doc)
  const observer = new MutationObserver(() => applyBuilderReadable(doc))
  observer.observe(doc.body, { attributes: true, attributeFilter: ['class', 'style'] })
  observer.observe(doc.documentElement, { attributes: true, attributeFilter: ['class', APP_THEME_ATTRIBUTE] })
  return () => {
    observer.disconnect()
    clearBuilderReadable(doc)
  }
}
